import * as React from 'react';
import { useAuth } from './authContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLangFromPath, buildPathWithLang } from './i18n/lang';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: 0,
  gap: 0,
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '420px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  borderRadius: 8,
  overflow: 'hidden'
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  background: 'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
}));

export default function SignIn() {
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation('common');
  const loc = useLocation();
  const lang = getLangFromPath(loc.pathname);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    let valid = true;
    if (!/\S+@\S+\.\S+/.test(data.get('email'))) {
      setEmailError(true);
      setEmailErrorMessage(t('forms.errors.invalid_email'));
      valid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }
    if (!data.get('password') || data.get('password').length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage(t('forms.errors.password_min', { min: 6 }));
      valid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }
    if (!valid) return;

    try {
      const result = await login({
        email: data.get('email'),
        password: data.get('password')
      });

      if (result.ok) {
        navigate(buildPathWithLang(lang, '/'), { replace: true });
        return;
      }

      const detail =
        result?.payload?.detail ||
        result?.payload?.message ||
        (result?.status === 403
          ? t('auth.email_not_confirmed')
          : t('auth.login_error'));
      setPasswordError(true);
      setPasswordErrorMessage(detail);
    } catch (err) {
      setPasswordError(true);
      setPasswordErrorMessage(`${t('auth.error_prefix')}: ${err.message}`);
    }
  };

  return (
    <>
      {/*<CssBaseline enableColorScheme />*/}
      <SignInContainer direction="column" alignItems="center">
        <Box mt={2} width="100%">
          <Card variant="outlined">
            {/* Header */}
            <div style={{
              background: "rgb(0,110,69)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 32,
              padding: "22px 32px",
              borderRadius: "8px 8px 0 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <span>{t('auth.login')}</span>
              <button
                type="button"
                onClick={() => {
                  const canGoBack =
                    typeof window !== 'undefined' && window.history && window.history.length > 1;
                  if (canGoBack) {
                    navigate(-1);
                  } else {
                    navigate(buildPathWithLang(lang, "/"));
                  }
                }}
                aria-label={t('common.close')}
                title={t('common.close')}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  fontSize: 34,
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: 0,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>
            {/* Форма внутри Card */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: 'auto',
                gap: 2,
                padding: "32px 32px 12px 32px"
              }}
            >
              <FormControl>
                <TextField
                  error={emailError}
                  helperText={emailErrorMessage}
                  id="email"
                  type="email"
                  name="email"
                  placeholder={t('forms.labels.email')}
                  autoComplete="email"
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                  color={emailError ? 'error' : 'primary'}
                  sx={{ mb: 2, borderRadius: 1, '& input': { height: 22, fontSize: 18 } }}
                />
              </FormControl>
              <FormControl>
                <TextField
                  error={passwordError}
                  helperText={passwordErrorMessage}
                  name="password"
                  placeholder={t('forms.labels.password')}
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  required
                  fullWidth
                  variant="outlined"
                  color={passwordError ? 'error' : 'primary'}
                  sx={{ mb: 3, borderRadius: 1, '& input': { height: 22, fontSize: 18 } }}
                />
              </FormControl>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  background: "rgb(0,110,69)",
                  fontSize: 22,
                  height: 54,
                  fontWeight: 600,
                  borderRadius: 1,
                  letterSpacing: 1.2,
                  mb: 1,
                  mt: 1,
                  '&:hover': { background: "#006b45" }
                }}
              >
                {t('auth.login').toUpperCase()}
              </Button>
              <Typography sx={{ textAlign: 'center', mt: 1 }}>
                <Link component={RouterLink} to={buildPathWithLang(lang, '/forgot-password')} variant="body2" sx={{ color: "rgb(0,110,69)", textDecoration: "underline", fontSize: 16 }}>
                  {t('forgot.link')}
                </Link>
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ textAlign: 'center', pb: 2, fontSize: 15 }}>
              {t('auth.no_account')}{' '}
              <Link
                component="button"
                variant="body2"
                sx={{ color: "rgb(0,110,69)", fontWeight: 500, fontSize: 15, textDecoration: "none" }}
                onClick={() => navigate(buildPathWithLang(lang, "/register"))}
              >
                {t('auth.register')}
              </Link>
            </Typography>
          </Card>
        </Box>
      </SignInContainer>
    </>
  );
}
